import { Component, computed, effect, inject, signal, untracked } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ForestPreset } from '../../theme/theme-presets';
import { APP_THEME_CONFIG } from '../../app.constants';

// PrimeNG Modules
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { PopoverModule } from 'primeng/popover';
import { ButtonModule } from 'primeng/button';
import { AvatarModule } from 'primeng/avatar';
import { BadgeModule } from 'primeng/badge';
import { RippleModule } from 'primeng/ripple';
import { TooltipModule } from 'primeng/tooltip';

// Theme Utilities
import { updatePrimaryPalette, usePreset } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';
import Lara from '@primeuix/themes/lara';
import { LayoutService } from '../../services/layout.service';
import { BreadcrumbService } from '../../services/breadcrumb.service';
import { AuthService, UserRole } from '../../core/services/auth.service';
import { ApiService } from '../../core/services/api.service';
import { environment } from '../../../environments/environment';
import { NotificationStateService } from '../../core/services/notification-state.service';
import { AppNotification, NotificationType } from '../../core/models/notification.model';
import Keycloak from 'keycloak-js';


interface NotificationVm extends AppNotification {
  timeAgo: string;
  icon: string;
  iconClass: string;
  bgClass: string;
}

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [
    FormsModule,
    RouterModule,
    BreadcrumbModule,
    PopoverModule,
    ButtonModule,
    AvatarModule,
    BadgeModule,
    RippleModule,
    TooltipModule
  ],
  templateUrl: './header.component.html'
})
export class HeaderComponent {
  layoutService = inject(LayoutService);
  breadcrumbService = inject(BreadcrumbService);
  public authService = inject(AuthService);
  private router = inject(Router);
  private api = inject(ApiService);
  notifService = inject(NotificationStateService);
  private keycloak = inject(Keycloak);

  // Avatar initials should read from the logged-in user's calling name (first 2 letters),
  // not the resolved display name — resolved via a lightweight employee lookup since neither
  // the mock nor the Keycloak user-info path carries calling_name on its own.
  private resolvedCallingInitials = signal<string | null>(null);

  // --- UI State ---
  isDarkMode = this.layoutService.isDarkMode;
  breadcrumbs = this.breadcrumbService.breadcrumbs;
  activePreset: string = APP_THEME_CONFIG.defaultPreset;
  activePrimary: string = APP_THEME_CONFIG.defaultPrimary;

  // DEV ONLY — true only when authMode = 'mock'.
  // Use this flag in the template to gate the role switcher panel:
  //   *ngIf="showDevTools"
  // In staging/prod this compiles to false and the panel is tree-shaken away.
  readonly showDevTools = environment.authMode === 'mock';

  constructor() {
    effect(() => {
      const empId = this.authService.loggedEmployeeId();
      untracked(() => {
        this.resolvedCallingInitials.set(null);
        if (!empId) return;
        const baseUrl = (environment as any).baseUrl ?? environment.apiUrl;
        this.api.get<any>(`${baseUrl}/employees/${empId}`).subscribe({
          next: (res: any) => {
            const emp = res?.data ?? res;
            const callingName: string = emp?.core_attributes?.calling_name ?? emp?.calling_name ?? '';
            this.resolvedCallingInitials.set(callingName.trim() ? callingName.trim().slice(0, 2).toUpperCase() : null);
          },
          error: () => this.resolvedCallingInitials.set(null)
        });
      });
    });
  }

   

  currentUserInfo = computed(() => {
    const callingInitials = this.resolvedCallingInitials();

    if (environment.authMode === 'keycloak') {
      const user = this.authService.currentUser();
      if (user) {
        const isAdmin = user.roles.includes('hr_admin') || user.roles.includes('admin') || user.roles.includes('enable_employee_approval_review');
        const isIntern = user.roles.includes('intern_hr');

        let roleLabel = 'Employee';
        if (isAdmin) {
          roleLabel = 'Arrow HR Admin';
        } else if (isIntern) {
          roleLabel = 'Intern HR';
        }

        const parts = user.name.trim().split(/\s+/);
        const fallbackAvatarLabel = parts.length > 1
          ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
          : user.name.slice(0, 2).toUpperCase();

        return {
          name: user.name,
          email: user.email,
          avatarLabel: callingInitials ?? fallbackAvatarLabel,
          roleLabel: roleLabel
        };
      }

      // No resolved user (e.g. mid-logout, or the profile hasn't loaded yet) — show a
      // neutral placeholder instead of falling through to the dev-mode mock names below,
      // which previously flashed "John Doe" for a moment during logout.
      return {
        name: '',
        email: '',
        avatarLabel: callingInitials ?? '',
        roleLabel: ''
      };
    }

    // DEV ONLY — mock-mode fallback below, keyed off the role switcher. Never reached
    // when authMode === 'keycloak'.
    const role = this.authService.currentUserRole();
    switch (role) {
      case 'hr_admin':
        return {
          name: 'HR Administrator',
          email: 'hr.admin@arrowhr.com',
          avatarLabel: callingInitials ?? 'HA',
          roleLabel: 'HR Admin'
        };
      case 'intern_hr':
        return {
          name: 'Intern HR',
          email: 'intern.hr@arrowhr.com',
          avatarLabel: callingInitials ?? 'IH',
          roleLabel: 'Intern HR'
        };
      case 'employee':
      default:
        return {
          name: 'John Doe',
          email: 'john.doe@arrowhr.com',
          avatarLabel: callingInitials ?? 'JD',
          roleLabel: 'Employee'
        };
    }
  });

  // DEV ONLY — called by the role switcher panel (visible only when showDevTools).
  // REMOVE WHEN IAM CONNECTED.
  switchRole(role: UserRole) {
    this.authService.setRole(role);
  }

  // --- Notification view models ---
  notificationVms = computed<NotificationVm[]>(() =>
    this.notifService.notifications().map(n => ({
      ...n,
      timeAgo: this.timeAgo(n.createdAt),
      icon: this.iconFor(n.type),
      iconClass: this.iconClassFor(n.type),
      bgClass: this.bgClassFor(n.type)
    }))
  );

  // --- Theme Data ---
  presets = ['Aura', 'Lara', 'Forest'];

  primaryColors = [
    { name: 'emerald', palette: '{emerald}', hex: '#10b981' },
    { name: 'green', palette: '{green}', hex: '#22c55e' },
    { name: 'lime', palette: '{lime}', hex: '#84cc16' },
    { name: 'orange', palette: '{orange}', hex: '#f97316' },
    { name: 'amber', palette: '{amber}', hex: '#f59e0b' },
    { name: 'yellow', palette: '{yellow}', hex: '#eab308' },
    { name: 'teal', palette: '{teal}', hex: '#14b8a6' },
    { name: 'cyan', palette: '{cyan}', hex: '#06b6d4' },
    { name: 'sky', palette: '{sky}', hex: '#0ea5e9' },
    { name: 'blue', palette: '{blue}', hex: '#3b82f6' },
    { name: 'indigo', palette: '{indigo}', hex: '#6366f1' },
    { name: 'violet', palette: '{violet}', hex: '#8b5cf6' },
    { name: 'purple', palette: '{purple}', hex: '#a855f7' },
    { name: 'fuchsia', palette: '{fuchsia}', hex: '#d946ef' },
    { name: 'pink', palette: '{pink}', hex: '#ec4899' },
    { name: 'rose', palette: '{rose}', hex: '#f43f5e' }
  ];

  // --- Notification actions ---
  onNotifClick(id: string, actionUrl: string | null, popover: { hide: () => void }): void {
    this.notifService.markRead(id);
    if (actionUrl) {
      popover.hide();
      void this.router.navigateByUrl(actionUrl);
    }
  }

  markAllRead(): void {
    this.notifService.markAllRead();
  }

  private iconFor(type: NotificationType): string {
    const map: Record<NotificationType, string> = {
      self_review_open: 'pi pi-user-edit',
      evaluator_turn: 'pi pi-clipboard',
      cycle_complete: 'pi pi-check-circle'
    };
    return map[type];
  }

  private iconClassFor(type: NotificationType): string {
    const map: Record<NotificationType, string> = {
      self_review_open: 'notif-icon-review',
      evaluator_turn: 'text-primary-600 dark:text-primary-400',
      cycle_complete: 'notif-icon-complete'
    };
    return map[type];
  }

  private bgClassFor(type: NotificationType): string {
    const map: Record<NotificationType, string> = {
      self_review_open: 'notif-bg-review',
      evaluator_turn: 'bg-primary-100 dark:bg-primary-900/30',
      cycle_complete: 'notif-bg-complete'
    };
    return map[type];
  }

  private timeAgo(date: Date): string {
    const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
    if (seconds < 60) return 'Just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
  }

  // --- Theme Switching Logic ---
  toggleDarkMode() {
    this.layoutService.toggleTheme();
  }

  changePreset(presetName: string) {
    this.activePreset = presetName;
    switch (presetName) {
      case 'Aura': usePreset(Aura); break;
      case 'Lara': usePreset(Lara); break;
      case 'Forest': usePreset(ForestPreset); break;
    }
    this.reapplyPalettes();
  }

  changePrimary(color: { name: string; palette: string; hex: string }) {
    this.activePrimary = color.name;
    updatePrimaryPalette(this.generatePaletteMap(color.palette));
  }

  private generatePaletteMap(baseToken: string) {
    return {
      50: baseToken.replace('}', '.50}'),
      100: baseToken.replace('}', '.100}'),
      200: baseToken.replace('}', '.200}'),
      300: baseToken.replace('}', '.300}'),
      400: baseToken.replace('}', '.400}'),
      500: baseToken.replace('}', '.500}'),
      600: baseToken.replace('}', '.600}'),
      700: baseToken.replace('}', '.700}'),
      800: baseToken.replace('}', '.800}'),
      900: baseToken.replace('}', '.900}'),
      950: baseToken.replace('}', '.950}')
    };
  }

  private reapplyPalettes() {
    const primary = this.primaryColors.find(c => c.name === this.activePrimary);
    if (primary) this.changePrimary(primary);
  }

  // --- Navigation ---
  async handleLogout() {
    this.authService.clearLocalSession();
    await this.keycloak.logout({
      redirectUri: window.location.origin + '/'
    });
  }

  goToProfile() {
    this.router.navigate(['/profile']);
  }
}
